import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import EmailSignInForm from './EmailSignInForm';
import { useLocation, Link } from 'react-router-dom';
import { Headphones, Pause, Play, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Footer from './Footer';
import { HomeStructuredData } from './StructuredData';
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectCards, Autoplay } from 'swiper/modules';
import Testimonials from './Testimonials';
import 'swiper/css';
import 'swiper/css/effect-cards';
import styles from './AudioImageCarousel.module.css';

const faqs = [
  {
    question: "Wie funktioniert Plottie?",
    answer: "Du beantwortest ein paar einfache Fragen über dein Kind, und wir erstellen daraus eine personalisierte Geschichte. Die fertige Hörgeschichte kannst du dann direkt abspielen, herunterladen oder auf einen Kreativ-Tonie übertragen."
  },
  {
    question: "Was kostet Plottie?", 
    answer: "Eine personalisierte Hörgeschichte kostet 3,90€. Damit erhältst du eine einzigartige Geschichte, die genau auf dein Kind zugeschnitten ist. Hör mal in unserer Beispiel-Hörgeschichte!"
  },
  {
    question: "Kann ich meine Geschichte vor dem Kauf lesen?",
    answer: "Ja, du erhältst eine Vorschau deiner Geschichte nach der Beantwortung der Fragen. So kannst du dir ein genaues Bild machen, worum es in der Geschichte geht und ob sie deinem Kind gefallen wird."
  },
  {
    question: "Was passiert, wenn mir die Vorschau nicht gefällt?",
    answer: "Kein Problem! Du kannst einmalig kostenlos eine neue Vorschau von Plottie erstellen lassen. So erhältst du eine Alternative mit einer anderen spannenden Handlung, die vielleicht besser zu deinen Vorstellungen passt."
  },
  {
    question: "Wie schnell ist meine Hörgeschichte fertig?",
    answer: "Die Erstellung einer Geschichte dauert nur wenige Minuten. Sobald du die Fragen beantwortet hast, machen wir uns daran, deine Geschichte umgehend zu erstellen."
  },
  {
    question: "Funktioniert Plottie mit meiner Tonie-box?",
    answer: "Ja! Plottie-Hörgeschichten sind kompatibel mit Kreativ-Tonies. Du bekommst eine .mp3 Datei, die du wie gewohnt von der Tonie-Cloud auf deine Kreativ-Tonies übertragen kannst."
  },
  {
    question: "Wie lang sind die Hörgeschichten?",
    answer: "Unsere Hörgeschichten sind aktuell etwa 5 Minuten lang – perfekt für eine kleine Auszeit zwischendurch oder als Teil eurer Gute-Nacht-Routine. Wir arbeiten bereits daran, in Zukunft auch längere Geschichten mit mehreren Kapiteln anzubieten. Genau danach suchst du? Schreib uns gerne eine E-Mail an hi@plottie.de!"
  },
  {
    question: "Für welches Alter sind die Geschichten gedacht?",
    answer: "Gleich zu Beginn fragen wir nach dem Alter deines Kindes, damit wir die Geschichte optimal darauf abstimmen können. So stellen wir sicher, dass sowohl die Erzählweise als auch der Inhalt genau dem Entwicklungsstand deines Kindes entsprechen."
  },
  {
    question: "Bleiben meine Geschichten dauerhaft verfügbar?",
    answer: "Ja! Nach dem Kauf kannst du ganz einfach einen kostenlosen Plottie-Account erstellen. Damit hast du alle deine Geschichten immer griffbereit – zum Anhören, Herunterladen oder erneuten Übertragen auf deine Kreativ-Tonies."
  }
];

const FAQAccordion = ({ question, answer, isOpen, onClick }) => {
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="border-b border-[#343434]/10"
    >
      <button
        className="w-full py-6 flex justify-between items-center text-left group"
        onClick={onClick}
      >
        <h3 className="font-freeman text-2xl md:text-[2rem] mb-2 text-[#343434] leading-[110%] tracking-[-0.03rem] md:tracking-[-0.05rem]">{question}</h3>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        >
          <ChevronDown className="w-5 h-5 text-[#343434]" />
        </motion.div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ 
              type: "spring",
              stiffness: 300,
              damping: 30
            }}
            className="overflow-hidden"
          >
            <div className="pb-8 text-[#343434] text-md leading-relaxed max-w-[70ch] break-words font-aleo">
              {answer}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const RotatingText = () => {
  const texts = [
    "100% Individuell",
    "Fantastische Erlebnisse",
    "Mit Herz und Botschaft",
    "Einfach & Flexibel"
  ];
  
  const [currentIndex, setCurrentIndex] = useState(0);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % texts.length);
    }, 4000);
    
    return () => clearInterval(interval);
  }, [texts.length]);

  const container = {
    hidden: { opacity: 0 },
    visible: (i = 1) => ({
      opacity: 1,
      transition: { staggerChildren: 0.03, delayChildren: 0.04 * i },
    }),
    exit: {
      opacity: 0,
      transition: { staggerChildren: 0.03, staggerDirection: -1 }
    }
  };

  const child = {
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        damping: 12,
        stiffness: 200,
      },
    },
    hidden: {
      opacity: 0,
      y: 20,
      transition: {
        type: "spring",
        damping: 12,
        stiffness: 200,
      },
    },
  };

  return (
    <div className="relative h-[6rem] overflow-visible">
      <AnimatePresence mode="wait">
        <motion.div
          key={currentIndex}
          variants={container}
          initial="hidden"
          animate="visible"
          exit="exit"
          className="absolute w-full text-center flex justify-center flex-wrap"
        >
          {texts[currentIndex].split("").map((char, index) => (
            <motion.span
              key={index}
              variants={child}
              className="inline-block"
            >
              {char === " " ? "\u00A0" : char}
            </motion.span>
          ))}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

// Add these variants for the animations
const heroVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut"
    }
  }
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.3
    }
  }
};

const carouselVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut",
      delay: 0.4 // Delay after hero section
    }
  }
};

const buttonVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut",
      delay: 0.6 // Delay after carousel
    }
  }
};

const emailFormVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut",
      delay: 0.8 // Delay after button
    }
  }
};

// Add these new variants near the other variant definitions
const howItWorksVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};

const stepsContainerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.3
    }
  }
};

// Add these variants near the other variant definitions at the top
const exampleSectionVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

const exampleTitleVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};

const exampleCardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut"
    }
  }
};

// Add these variants near the other variant definitions at the top
const mobileCardStackVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut",
      delay: 0.4 // Similar delay as the carousel
    }
  }
};

// AUDIO SOURCES
function Landing() {
  const stories = [
    {
      title: "Das Rätsel der tanzenden Teekannen",
      image: "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/05-rYUpqcrfrhrGYKlzelXjuAFjNCUHf1.png",
      audioUrl: '/Das-Rätsel-der-tanzenden-Teekannen.m4a'
    },
    {
      title: "Abenteuer im Flüsterwald",
      image: "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/01-Tg5ZGqvpjxErai0lMqaHkGclvKqwie.png",
      audioUrl: '/Abenteuer-im-Fluesterwald.m4a'
    },
    {
      title: "Die Geschichte vom Pudding-Planeten",
      image: "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/04-iK8g04vZIYZHaQI7ml3jHR0gtIlP6X.png",
      audioUrl: '/Die-Geschichte-vom-Puddingplaneten.m4a'
    },
    {
      title: "Fenja und Bruno am Nordpol",
      image: "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/03-caAOQNyqGWKClgpbCFzpU7LanYaK3L.png",
      audioUrl: '/Fenja-und-Bruno-am-Nordpol.m4a'
    },
    {
      title: "Finn und die Korallenstadt",
      image: "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/02-kOrULscPeqi3AbP3I7tDqJW8PZlH4A.png",
      audioUrl: '/Finn-und-die-Korallenstadt.m4a'
    },
  ];

  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');
  const { authError, emailLinkError } = useAuth();
  const location = useLocation();
  const [activeImageIndex, setActiveImageIndex] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [intervalId, setIntervalId] = useState(null);

  const [isVisible, setIsVisible] = useState(true);

  const displayError = emailLinkError || location.state?.emailLinkError;

  const [isScrubbing, setIsScrubbing] = useState(false);

  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  const [isHeroVisible, setIsHeroVisible] = useState(true);
  const heroRef = useRef(null);

  const [activeAudioIndex, setActiveAudioIndex] = useState(null);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);
  const audioRef = useRef(null);

  const swiperRef = useRef();
  const [swiperActiveIndex, setSwiperActiveIndex] = useState(0);

  // Add this state near the top of the component
  const [isDesktop, setIsDesktop] = useState(false);

  // Add this useEffect to handle window resize
  useEffect(() => {
    const checkIsDesktop = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };
    
    // Check initially
    checkIsDesktop();
    
    // Add resize listener
    window.addEventListener('resize', checkIsDesktop);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkIsDesktop);
  }, []);

  useEffect(() => {
    let timer;
    if (activeImageIndex !== null) {
      timer = setTimeout(() => {
        setActiveImageIndex(null);
      }, 2000);
    }
    return () => clearTimeout(timer);
  }, [activeImageIndex]);

  const handleImageClick = (index, event) => {
    event.stopPropagation(); // Prevent click from bubbling to document
    
    // Simply toggle the active state without affecting the animation
    if (activeImageIndex === index) {
      setActiveImageIndex(null);
    } else {
      setActiveImageIndex(index);
    }
  };

  useEffect(() => {
    const handleDocumentClick = () => {
      setActiveImageIndex(null);
    };

    document.addEventListener('click', handleDocumentClick);
    return () => document.removeEventListener('click', handleDocumentClick);
  }, []);

  // Define the audio element states
  const [audioElement, setAudioElement] = useState(null);
  const [exampleAudioElement, setExampleAudioElement] = useState(null);

  // Initialize both audio elements
  useEffect(() => {
    const mainAudio = new Audio('/Lina-in-Pinguinopolis.m4a');
    setAudioElement(mainAudio);

    const exampleAudio = new Audio('/Abenteuer-im-Fluesterwald.m4a');
    setExampleAudioElement(exampleAudio);

    return () => {
      mainAudio.pause();
      exampleAudio.pause();
    };
  }, []);

  // Update the click handler for the main audio play button
  const handlePlayClick = () => {
    if (!audioElement) return;
    
    setIsPlaying(!isPlaying);
    
    if (isPlaying) {
      audioElement.pause();
      if (intervalId) {
        clearInterval(intervalId);
        setIntervalId(null);
      }
    } else {
      setIsVisible(true);
      audioElement.play();
      
      const interval = setInterval(() => {
        const progress = (audioElement.currentTime / audioElement.duration) * 100;
        setProgress(progress);
        
        if (progress >= 100) {
          clearInterval(interval);
          setIsPlaying(false);
          setIntervalId(null);
          setIsVisible(false);
          setTimeout(() => {
            setProgress(0);
          }, 300);
        }
      }, 50);
      
      setIntervalId(interval);
    }
  };

  // Add a separate click handler for the example section play button
  const handleExamplePlayClick = () => {
    if (!exampleAudioElement) return;
    
    setIsPlaying(!isPlaying);
    
    if (isPlaying) {
      exampleAudioElement.pause();
      if (intervalId) {
        clearInterval(intervalId);
        setIntervalId(null);
      }
    } else {
      exampleAudioElement.play();
      
      const interval = setInterval(() => {
        const progress = (exampleAudioElement.currentTime / exampleAudioElement.duration) * 100;
        setProgress(progress);
        
        if (progress >= 100) {
          clearInterval(interval);
          setIsPlaying(false);
          setIntervalId(null);
          setTimeout(() => {
            setProgress(0);
          }, 300);
        }
      }, 50);
      
      setIntervalId(interval);
    }
  };

  const progressBarRef = useRef(null);

  const handleScrub = useCallback((event) => {
    if (!audioElement || !progressBarRef.current) return;

    const rect = progressBarRef.current.getBoundingClientRect();
    const offsetX = event.clientX - rect.left;
    const newProgress = (offsetX / rect.width) * 100;
    const newTime = (newProgress / 100) * audioElement.duration;

    audioElement.currentTime = newTime;
  }, [audioElement]); // Dependencies array includes audioElement since it's used inside

  // Add event listeners for scrubbing
  useEffect(() => {
    const handleMouseMove = (event) => {
      if (isScrubbing) {
        handleScrub(event);
      }
    };

    const handleMouseUp = () => {
      setIsScrubbing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isScrubbing, handleScrub]);

  // Add a ref to the carousel container
  const carouselRef = useRef(null);

  const handleScrollLeft = () => {
    if (!carouselRef.current) return;
    
    const container = carouselRef.current;
    const cardWidth = container.querySelector('div[class*="w-[23.125rem]"]')?.offsetWidth || 0;
    
    // Calculate the new scroll position
    const newScrollLeft = Math.max(0, container.scrollLeft - cardWidth);
    
    container.scrollTo({
      left: newScrollLeft,
      behavior: 'smooth'
    });
  };

  const handleScrollRight = () => {
    if (!carouselRef.current) return;
    
    const container = carouselRef.current;
    const cardWidth = container.querySelector('div[class*="w-[23.125rem]"]')?.offsetWidth || 0;
    
    // Calculate the maximum scroll position
    const maxScroll = container.scrollWidth - container.clientWidth;
    // Calculate the new scroll position
    const newScrollLeft = Math.min(maxScroll, container.scrollLeft + cardWidth);
    
    container.scrollTo({
      left: newScrollLeft,
      behavior: 'smooth'
    });
  };

  // Simplified scroll position tracking
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      const container = carouselRef.current;
      if (!container) return;

      // Check if we can scroll in either direction
      setCanScrollLeft(Math.ceil(container.scrollLeft) > 0);
      setCanScrollRight(
        Math.floor(container.scrollLeft + container.clientWidth) < container.scrollWidth
      );
    };

    const container = carouselRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      // Check initial scroll state
      handleScroll();
      
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, []);

  const [activeCardIndex, setActiveCardIndex] = useState(0);

  // Eyebrow texts for each card
  const eyebrowTexts = [
    "100% INDIVIDUELL",
    "FANTASTISCHE ERLEBNISSE",
    "MIT HERZ UND BOTSCHAFT",
    "EINFACH & FLEXIBEL"
  ];

  useEffect(() => {
    const handleScroll = () => {
      const container = carouselRef.current;
      if (!container) return;

      const cards = container.querySelectorAll('.carousel-card');
      const containerCenter = container.offsetWidth / 2;

      cards.forEach((card, index) => {
        const cardRect = card.getBoundingClientRect();
        const cardCenter = cardRect.left + cardRect.width / 2;
        const isCenter = Math.abs(containerCenter - cardCenter) < cardRect.width / 2;

        if (isCenter) {
          setActiveCardIndex(index);
        }
      });
    };

    const container = carouselRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      handleScroll(); // Initial check

      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsHeroVisible(entry.isIntersecting);
      },
      {
        threshold: 0.1, // Trigger when 10% of the hero is visible
        rootMargin: '-80px 0px 0px 0px' // Offset for the navbar height
      }
    );

    if (heroRef.current) {
      observer.observe(heroRef.current);
    }

    return () => {
      if (heroRef.current) {
        observer.unobserve(heroRef.current);
      }
    };
  }, []);

  // Add this effect to handle audio playback
  useEffect(() => {
    const audioElement = audioRef.current;
    
    let progressInterval;
    if (audioElement && isAudioPlaying) {
      progressInterval = setInterval(() => {
        const currentProgress = (audioElement.currentTime / audioElement.duration) * 100;
        setAudioProgress(currentProgress);
      }, 50);
    }

    const handleAudioEnd = () => {
      setIsAudioPlaying(false);
      setAudioProgress(0);
      setActiveAudioIndex(null);
    };

    if (audioElement) {
      audioElement.addEventListener('ended', handleAudioEnd);
    }

    return () => {
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      if (audioElement) {
        audioElement.removeEventListener('ended', handleAudioEnd);
      }
    };
  }, [isAudioPlaying]);

  // Add this function to handle audio playback
  const handleAudioPlay = (index) => {
    if (!audioRef.current) return;

    if (activeAudioIndex === index && isAudioPlaying) {
      audioRef.current.pause();
      setIsAudioPlaying(false);
    } else {
      if (activeAudioIndex !== index) {
        audioRef.current.src = stories[index % stories.length].audioUrl;
        audioRef.current.currentTime = 0;
        setAudioProgress(0);
      }
      audioRef.current.play();
      setIsAudioPlaying(true);
      setActiveAudioIndex(index);
    }
  };

  // Add this useEffect to handle initial scroll when component mounts
  useEffect(() => {
    // Get hash from URL
    const hash = location.hash;
    if (hash) {
      // Remove the '#' and scroll to element
      const id = hash.substring(1);
      const element = document.getElementById(id);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location.hash]); // Depend on location.hash to re-run if hash changes

  // Add this function near the top of your file, before the Landing component
  const trackEvent = (category, label, value) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', label, {
        // GA4 parameters
        event_category: category,
        event_label: label,
        value: value
      });
    }
  };

  return (
    <div className="landing-page min-h-screen w-full overflow-x-hidden m-0 p-0">
      <nav className="fixed top-0 z-50 w-full">
        <div 
          className="w-full"
          style={{
            backgroundImage: 'url(/nav-bg.svg)',
            backgroundSize: 'auto 120%',
            backgroundPosition: 'center',
            backgroundRepeat: 'repeat',
          }}
        >
          <div className="max-w-full sm:max-w-[90%] xl:max-w-[70rem] mx-auto px-4 sm:px-6 xl:px-6 relative">
            <div className="flex items-center justify-between h-16 lg:h-24">
              {/* Logo - always on the left for desktop */}
              <div className="hidden lg:block">
                <Link to="/" className="flex items-center">
                  <img 
                    src="/Logo.svg" 
                    alt="Plottie Logo" 
                    className="h-12 w-auto"
                  />
                </Link>
              </div>

              {/* Mobile logo with fade and slide animation */}
              <div className="lg:hidden"> {/* Container for mobile logos */}
                {/* Centered mobile logo */}
                <div 
                  className={`absolute left-1/2 top-1/2 transform -translate-x-1/2 ${
                    isHeroVisible 
                      ? 'opacity-100 -translate-y-1/2' 
                      : 'opacity-0 -translate-y-full'
                  }`}
                  style={{
                    transition: 'all 600ms cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                >
                  <Link to="/" className="flex items-center">
                    <img 
                      src="/Logo.svg" 
                      alt="Plottie Logo" 
                      className="h-8 w-auto"
                    />
                  </Link>
                </div>

                {/* Left-aligned mobile logo */}
                <div 
                  className={`absolute left-4 sm:left-6 top-1/2 transform ${
                    isHeroVisible 
                      ? 'opacity-0 -translate-y-full' 
                      : 'opacity-100 -translate-y-1/2'
                  }`}
                  style={{
                    transition: 'all 600ms cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                >
                  <Link to="/" className="flex items-center">
                    <img 
                      src="/Logo.svg" 
                      alt="Plottie Logo" 
                      className="h-8 w-auto"
                    />
                  </Link>
                </div>
              </div>
              
              {/* Button - always visible on desktop */}
              <div className="hidden lg:block">
                <a 
                  href="/erstellen"
                  onClick={() => {
                    trackEvent('engagement', 'create_story_button_nav', 1);
                  }}
                  className={`px-5 py-3 rounded-full font-freeman text-lg md:tracking-[-0.04rem] transition-all duration-300 border-2 border-[#FFA23E] ${
                    isHeroVisible 
                      ? 'bg-transparent text-[#343434] hover:bg-[#FFA23E] hover:text-[#343434]' 
                      : 'bg-[#FFA23E] text-[#343434] hover:bg-[#ff9625]'
                  }`}
                >
                  Gratis Vorschau erstellen
                </a>
              </div>

              {/* Mobile button with fade animation */}
              <div 
                className={`lg:hidden ${
                  isHeroVisible ? 'opacity-0' : 'opacity-100'
                }`}
                style={{
                  transition: 'all 600ms ease-out'
                }}
              >
                <a 
                  href="/erstellen"
                  onClick={() => {
                    trackEvent('engagement', 'create_story_button_nav', 1);
                  }}
                  className="px-3 py-2 bg-[#FFA23E] text-[#343434] rounded-full font-freeman text-base hover:bg-[#ff9625] transition-all duration-300 tracking-[-0.03rem]"
                >
                  Gratis Vorschau erstellen
                </a>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <motion.div 
        ref={heroRef}
        className="sm:mx-0 pt-4"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
      >
        <div className="max-w-full sm:max-w-[90%] xl:max-w-[70rem] mx-auto px-4 sm:px-6 xl:px-6 mt-10 flex flex-col items-center md:h-auto md:justify-start">
          <div className="w-full text-center">
            <div className="max-w-[90%] md:max-w-[80%] mx-auto">
              <motion.h1 
                variants={heroVariants}
                className="text-[#343434] text-[2.8rem] sm:text-[3.25rem] md:text-[4.5rem] leading-[110%] tracking-[-0.105rem] md:tracking-[-0.135rem] font-freeman pt-2 md:pt-24 mb-3 md:mb-6"
              >
                Magische Hörgeschichten mit deinem Kind als Held*in.
              </motion.h1>
              <motion.p 
                variants={heroVariants}
                className="text-[#343434] text-base md:text-[1.25rem] font-normal leading-[150%] font-aleo"
              >
                Mit Plottie erstellst du ganz einfach personalisierte Abenteuer für Toniebox™ oder Lautsprecher. Nur 3,90€ für 5 Min. Hörspaß, mit gratis Vorschau!
              </motion.p>
              
              <motion.div 
                variants={heroVariants}
                className="relative z-10 mt-8 md:mt-12"
              >
                <div className="max-w-2xl mx-auto text-center flex flex-col items-center">
                  <a 
                    href="/erstellen"
                    onClick={() => {
                      trackEvent('engagement', 'create_story_button', 1);
                    }}
                    className="h-14 bg-[#FFA23E] text-[#343434] px-8 py-2 rounded-full font-freeman text-[1.25rem] font-normal leading-[110%] tracking-[-0.0375rem] inline-flex items-center justify-center hover:bg-[#FF9925] transition-colors duration-300"
                  >
                    Gratis Vorschau erstellen
                  </a>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Mobile Card Stack */}
      <motion.div 
        className="block md:hidden max-w-full sm:max-w-[90%] xl:max-w-[70rem] mx-auto px-4 sm:px-6 mt-6"
        variants={mobileCardStackVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
      >
        <div className={`${styles.carouselContainer} h-[360px] mb-[-80px]`}>
          <Swiper
            effect={'cards'}
            grabCursor={true}
            loop={true}
            modules={[EffectCards, Autoplay]}
            className={styles.mySwiper}
            autoplay={{
              delay: 2500,
              disableOnInteraction: false,
            }}
            cardsEffect={{
              perSlideOffset: 8,
              perSlideRotate: 2,
              rotate: true,
              slideShadows: false,
            }}
            speed={600}
            onSwiper={(swiper) => {
              swiperRef.current = swiper;
            }}
            onSlideChange={(swiper) => {
              setSwiperActiveIndex(swiper.realIndex);
              // Stop audio and reset when changing slides
              if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.currentTime = 0;
                setIsAudioPlaying(false);
                setAudioProgress(0);
                setActiveAudioIndex(null);
              }
            }}
          >
            {stories.map((story, index) => (
              <SwiperSlide 
                key={index} 
                className={styles.swiperSlide}
              >
                <img 
                  src={story.image} 
                  alt={story.title} 
                  className={styles.imgCover}
                  loading="lazy"
                />
                {index === swiperActiveIndex && (
                  <div className={styles.audioOverlay}>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        if (audioRef.current) {
                          if (isAudioPlaying) {
                            audioRef.current.pause();
                            swiperRef.current?.autoplay.start();
                          } else {
                            audioRef.current.src = story.audioUrl;
                            audioRef.current.play().then(() => {
                              setIsAudioPlaying(true);
                              swiperRef.current?.autoplay.stop();
                              trackEvent('engagement', 'play_audio_sample', story.title);
                            }).catch(error => {
                              console.error('Audio playback failed:', error);
                            });
                          }
                          setIsAudioPlaying(!isAudioPlaying);
                        }
                      }}
                      className={`${styles.playPauseButton} group relative overflow-hidden flex items-center gap-4 px-4 h-9`}
                      aria-label={isAudioPlaying && index === swiperActiveIndex ? 'Pause' : 'Play'}
                    >
                      <div 
                        className="absolute left-0 top-0 h-full w-full bg-[#7785FC] transition-all duration-100 ease-linear z-0"
                        style={{ width: `${audioProgress}%` }}
                      />
                      
                      <div className="relative z-10 transition-opacity duration-300 flex items-center gap-1.5">
                        {isAudioPlaying && index === swiperActiveIndex ? (
                          <Pause size={16} className="text-[#343434] fill-current" />
                        ) : (
                          <Play size={16} className="text-[#343434] fill-current" />
                        )}
                        <span className="text-[#343434] font-aleo text-[1rem] leading-[100%] whitespace-nowrap">
                          Hörprobe
                        </span>
                      </div>
                    </button>
                  </div>
                )}
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </motion.div>

      {/* Desktop Stories Carousel - hide on mobile */}
      <div className="hidden md:block">
        {/* Stories carousel*/}
        <motion.div
          className="relative max-w-full sm:max-w-[90%] xl:max-w-[70rem] mx-auto"
          variants={carouselVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          <div className="absolute inset-y-0 left-0 w-[100px] bg-gradient-to-r from-[#EDD1BA] to-transparent z-20 hidden md:block"></div>
          <div className="absolute inset-y-0 right-0 w-[100px] bg-gradient-to-l from-[#EDD1BA] to-transparent z-20 hidden md:block"></div>
          
          {/* Stories carousel */}
          <div className="flex overflow-hidden mt-4 h-[16rem] md:h-[28rem] md:pt-4">
            <div className="flex flex-col px-4 pt-4 pb-1 relative z-10 overflow-visible w-full">
              <div className="flex animate-scroll relative z-10 mb-4 justify-center backdrop-blur-[2px]">
                {/* First set */}
                {stories.map((story, index) => (
                  <div
                    key={index}
                    className={`group relative w-[7.5rem] md:w-[17rem] h-[10rem] md:h-[22rem] rounded-[1rem] md:rounded-[1.5rem] mx-1 md:mx-3 flex-shrink-0 transition-all duration-300 ease-in-out ${
                      activeImageIndex === index ? 'scale-110 z-10 mx-3 md:mx-6' : ''
                    } hover:scale-110 hover:z-10 hover:mx-3 md:hover:mx-6 origin-center peer-hover:mx-3 md:peer-hover:mx-6 overflow-hidden md:hover:scale-115`}
                    onClick={(e) => handleImageClick(index, e)}
                  >
                    <div className="w-full h-full">
                      <img 
                        src={story.image} 
                        alt={story.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      {/* Audio overlay */}
                      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 hidden md:block">
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAudioPlay(index);
                            if (!isAudioPlaying) {
                              trackEvent('engagement', 'play_audio_sample', stories[index].title);
                            }
                          }}
                          className="group flex items-center justify-center gap-1 rounded-full bg-[#EDD1BA]/80 px-4 py-2 text-[#343434] text-[0.875rem] font-bold leading-[150%] relative overflow-hidden hover:scale-110 transition-all duration-300"
                          style={{
                            fontFamily: 'Aleo',
                            fontWeight: 700,
                            lineHeight: '1.5rem',
                            fontStyle: 'normal',
                            textEdge: 'cap',
                            leadingTrim: 'both',
                          }}
                        >
                          {/* Progress bar overlay */}
                          {activeAudioIndex === index && (
                            <div 
                              className="absolute left-0 top-0 h-full bg-[#7785FC] transition-all duration-100 ease-linear z-0 rounded-full"
                              style={{ width: `${audioProgress}%` }}
                            />
                          )}
                          
                          {/* Button content */}
                          <div className="relative z-10 flex items-center justify-center gap-1">
                            <div className="transition-opacity duration-300">
                              {activeAudioIndex === index && isAudioPlaying ? (
                                <Pause size={14} className="relative bottom-[1px] fill-current" />
                              ) : (
                                <Play size={14} className="relative bottom-[1px] fill-current" />
                              )}
                            </div>
                            <span className="text-[#343434]">
                              Hörprobe
                            </span>
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                {/* Second set */}
                {stories.map((story, index) => (
                  <div
                    key={`duplicate-${index}`}
                    className={`group relative w-[7.5rem] md:w-[17rem] h-[10rem] md:h-[22rem] rounded-[1rem] md:rounded-[1.5rem] mx-1 md:mx-3 flex-shrink-0 transition-all duration-300 ease-in-out ${
                      activeImageIndex === index + stories.length ? 'scale-110 z-10 mx-3 md:mx-6' : ''
                    } hover:scale-110 hover:z-10 hover:mx-3 md:hover:mx-6 origin-center peer-hover:mx-3 md:peer-hover:mx-6 overflow-hidden md:hover:scale-115`}
                    onClick={(e) => handleImageClick(index + stories.length, e)}
                  >
                    <div className="w-full h-full">
                      <img 
                        src={story.image} 
                        alt={story.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      {/* Audio overlay */}
                      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 hidden md:block">
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAudioPlay(index + stories.length);
                            if (!isAudioPlaying) {
                              trackEvent('engagement', 'play_audio_sample', stories[index + stories.length].title);
                            }
                          }}
                          className="group flex items-center justify-center gap-1 rounded-full bg-[#EDD1BA]/80 px-4 py-2 text-[#343434] text-[0.875rem] font-bold leading-[150%] relative overflow-hidden hover:scale-110 transition-all duration-300"
                          style={{
                            fontFamily: 'Aleo',
                            fontWeight: 700,
                            lineHeight: '1.5rem',
                            fontStyle: 'normal',
                            textEdge: 'cap',
                            leadingTrim: 'both',
                          }}
                        >
                          {/* Progress bar overlay */}
                          {activeAudioIndex === index + stories.length && (
                            <div 
                              className="absolute left-0 top-0 h-full bg-[#7785FC] transition-all duration-100 ease-linear z-0 rounded-full"
                              style={{ width: `${audioProgress}%` }}
                            />
                          )}
                          
                          {/* Button content */}
                          <div className="relative z-10 flex items-center justify-center gap-1">
                            <div className="transition-opacity duration-300">
                              {activeAudioIndex === index + stories.length && isAudioPlaying ? (
                                <Pause size={14} className="relative bottom-[1px] fill-current" />
                              ) : (
                                <Play size={14} className="relative bottom-[1px] fill-current" />
                              )}
                            </div>
                            <span className="text-[#343434]">
                              Hörprobe
                            </span>
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {displayError && (
        <div className="text-red-500 mb-4">{displayError}</div>
      )}

      {/* How it works section */}
      <div id="so-funktionierts" className="w-screen relative left-[50%] right-[50%] mx-[-50vw] mt-20 lg:mt-40">
        <div className="max-w-full sm:max-w-[90%] xl:max-w-[100rem] mx-auto px-4 sm:px-6 xl:px-6 pb-20 mb-0 relative">
          {/* Header */}
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-[#7785FC] text-[1.125rem] md:text-[1.5rem] font-bold leading-normal font-['Edu_TAS_Beginner'] mb-2">
              LOS GEHT'S!
            </h2>
            <motion.h3 
              className="text-[#343434] text-[2.125rem] md:text-[4.5rem] leading-[110%] tracking-[-0.06375rem] md:tracking-[-0.135rem] font-freeman"
              initial="hidden"
              animate="visible"
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: {
                    staggerChildren: 0.03
                  }
                }
              }}
            >
              {["So erstellst du deine", "besondere Hörgeschichte"].map((line, lineIndex) => (
                <motion.span key={lineIndex} className="block">
                  {line.split("").map((char, charIndex) => (
                    <motion.span
                      key={charIndex}
                      variants={{
                        hidden: { opacity: 0, y: 20 },
                        visible: {
                          opacity: 1,
                          y: 0,
                          transition: {
                            type: "spring",
                            damping: 12,
                            stiffness: 200
                          }
                        }
                      }}
                      className="inline-block"
                    >
                      {char === " " ? "\u00A0" : char}
                    </motion.span>
                  ))}
                </motion.span>
              ))}
            </motion.h3>
          </div>

                    {/* Bento Grid - Mobile: stacked squares, Desktop: bento layout */}
          <motion.div 
            className="flex flex-col gap-4 w-full max-w-7xl mx-auto md:gap-8 lg:grid lg:grid-cols-6 lg:grid-rows-2 lg:gap-8 lg:h-[50rem]"
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: {
                  staggerChildren: 0.2,
                  delayChildren: 0.3
                }
              }
            }}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            {/* Card 1 - Top Left (1/2 width) */}
            <motion.div 
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: { 
                  opacity: 1,
                  y: 0,
                  transition: { type: "spring", damping: 25, stiffness: 100 }
                }
              }}
              className="bg-[#ECC4A3] rounded-[40px] p-6 lg:col-span-3 lg:row-span-1 aspect-video lg:aspect-auto"
            >
              <div className="flex flex-col h-full justify-between">
                <img 
                  src="https://i.imgur.com/y2OVCBY.png"
                  alt=""
                  className="w-32 h-32 object-contain" 
                  aria-hidden="true"
                  loading="lazy"
                />
                <div className="mt-auto">
                  <h4 
                    className="font-freeman text-[1.75rem] lg:text-[2rem] font-normal leading-[110%] tracking-[-0.05rem] text-[#343434] mb-4"
                    style={{
                      leadingTrim: 'both',
                      textEdge: 'cap',
                      fontStyle: 'normal'
                    }}
                  >
                    Ein paar einfache Fragen
                  </h4>
                  <p className="font-aleo text-[#343434] text-sm lg:text-base pr-16">
                    Wie soll dein Held heißen? Welche Botschaft soll vermittelt werden? Wo spielt die Geschichte?
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Card 2 - Top Right (2/2 width) */}
            <motion.div 
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: { 
                  opacity: 1,
                  y: 0,
                  transition: { type: "spring", damping: 25, stiffness: 100 }
                }
              }}
              className="bg-[#ECC4A3] rounded-[40px] p-6 lg:col-span-3 lg:row-span-1 aspect-video lg:aspect-auto"
            >
              <div className="flex flex-col h-full justify-between">
                <img 
                  src="https://i.imgur.com/sXzT0UT.png"
                  alt=""
                  className="w-32 h-32 object-contain" 
                  aria-hidden="true"
                  loading="lazy"
                />
                <div className="mt-auto">
                  <h4 
                    className="font-freeman text-[1.75rem] lg:text-[2rem] font-normal leading-[110%] tracking-[-0.05rem] text-[#343434] mb-4"
                    style={{
                      leadingTrim: 'both',
                      textEdge: 'cap',
                      fontStyle: 'normal'
                    }}
                  >
                    Gratis Vorschau erhalten<br/>& Stimme wählen
                  </h4>
                  <p className="font-aleo text-[#343434] text-sm lg:text-base pr-12">
                    Vor dem Kauf erhältst du gratis eine Vorschau der Geschichte und wählst aus professionellen Erzählstimmen die passende aus.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Card 3 - Bottom Left (1/3 width) */}
            <motion.div 
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: { 
                  opacity: 1,
                  y: 0,
                  transition: { type: "spring", damping: 25, stiffness: 100 }
                }
              }}
              className="bg-[#ECC4A3] rounded-[40px] p-6 lg:col-span-2 lg:row-span-1 aspect-video lg:aspect-auto"
            >
              <div className="flex flex-col h-full justify-between">
                <img 
                  src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/2-1bwb79z0QVvKonS50hNrurfFohBdpN.png"
                  alt=""
                  className="w-32 h-32 object-contain" 
                  aria-hidden="true"
                  loading="lazy"
                />
                <div className="mt-auto">
                  <h4 
                    className="font-freeman text-[1.75rem] lg:text-[2rem] font-normal leading-[110%] tracking-[-0.05rem] text-[#343434] mb-4"
                    style={{
                      leadingTrim: 'both',
                      textEdge: 'cap',
                      fontStyle: 'normal'
                    }}
                  >
                    Magische Geschichten entstehen
                  </h4>
                  <p className="font-aleo text-[#343434] text-sm lg:text-base">
                    Wir erstellen spannende, altersgerechte Abenteuer, die genau zu deinem Kind passen und groooßartig klingen!
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Card 4 - Bottom Center (2/3 width) */}
            <motion.div 
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: { 
                  opacity: 1,
                  y: 0,
                  transition: { type: "spring", damping: 25, stiffness: 100 }
                }
              }}
              className="bg-[#ECC4A3] rounded-[40px] p-6 lg:col-span-2 lg:row-span-1 aspect-video lg:aspect-auto"
            >
              <div className="flex flex-col h-full justify-between">
                <img 
                  src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/3-iryqAPpyWTvZVh8jMtkdaCBK1tCs95.png"
                  alt=""
                  className="w-32 h-32 object-contain" 
                  aria-hidden="true"
                  loading="lazy"
                />
                <div className="mt-auto">
                  <h4 
                    className="font-freeman text-[1.75rem] lg:text-[2rem] font-normal leading-[110%] tracking-[-0.05rem] text-[#343434] mb-4"
                    style={{
                      leadingTrim: 'both',
                      textEdge: 'cap',
                      fontStyle: 'normal'
                    }}
                  >
                    Für immer & überall
                  </h4>
                  <p className="font-aleo text-[#343434] text-sm lg:text-base">
                    Höre, lade herunter oder übertrage auf deinen Kreativ-Tonie. Mit deinem gratis Account bleiben deine Geschichten immer verfügbar.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Card 5 - Bottom Right (3/3 width) - Image Only */}
            <motion.div 
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: { 
                  opacity: 1,
                  y: 0,
                  transition: { type: "spring", damping: 25, stiffness: 100 }
                }
              }}
              className="bg-[#FFF3DD] rounded-[40px] overflow-hidden lg:col-span-2 lg:row-span-1 aspect-square lg:aspect-auto"
            >
              <img 
                src="https://i.imgur.com/8BbTwKk.png"
                alt="Plottie process illustration"
                className="w-full h-full object-cover" 
                onError={(e) => {
                  console.log('Image failed to load:', e.target.src);
                  e.target.style.backgroundColor = '#FFA23E';
                  e.target.style.opacity = '0.3';
                }}
                loading="eager"
              />
            </motion.div>
          </motion.div>

          {/* Button */}
          <div className="flex justify-center mt-8 md:mt-12 mb-4">
            <a 
              href="/erstellen"
              onClick={() => {
                trackEvent('engagement', 'create_story_button_how_it_works', 1);
              }}
              className="h-14 bg-[#FFA23E] text-[#343434] px-8 py-2 rounded-full font-freeman text-[1.25rem] font-normal leading-[110%] tracking-[-0.0375rem] inline-flex items-center justify-center hover:bg-[#FF9925] transition-all duration-300 hover:scale-105"
            >
              Jetzt gratis Vorschau erstellen
            </a>
          </div>
        </div>
      </div>

      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={containerVariants}
        className="relative"
      >
        <Testimonials />
        
        {/* Bottom decorative image - moved inside the section and removed absolute positioning */}
        <div id="beispiele" className="w-screen relative left-[50%] right-[50%] mx-[-50vw]">
          <img 
            src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Section-Banner_Bottom-Orange-dyTvkASnVbaDA9lES0AomaVIvb4tlC.png"
            className="md:hidden w-full pointer-events-none select-none translate-y-[1px]"
            aria-hidden="true"
            alt=""
            loading="lazy"
          />
          <img 
            src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Section-Banner_Top-Orange-W2ZYP9ED9O6V7uhXp0vrc5HbOeYJHe.png"
            className="hidden md:block w-full pointer-events-none select-none rotate-180 translate-y-[1px]"
            aria-hidden="true"
            alt=""
            loading="lazy"
          />
        </div>
      </motion.section>

      {/* Example section */}
      <div className="w-screen relative left-[50%] right-[50%] mx-[-50vw] bg-[#FFA23E] py-20 lg:pb-60 mb-0">
        {/* Add the background image container */}
        <div className="hidden lg:block absolute inset-0 w-full h-full overflow-hidden">
          <img 
            src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/example-bg-large-K2yv9C834KJ4irLHnQ9jEEtdpbvrjl.png"
            className="w-full h-full object-contain pointer-events-none select-none absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 scale-[0.60]"
            aria-hidden="true"
            alt=""
            loading="lazy"
          />
        </div>

        <motion.div
          className="max-w-4xl mx-auto px-6 relative"
          variants={exampleSectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          <motion.div variants={exampleTitleVariants}>
            <h2 className="text-white text-[1.125rem] md:text-[1.5rem] font-bold leading-normal text-center font-['Edu_TAS_Beginner'] mb-2 md:mb-0 flex items-center justify-center gap-2">
              JETZT PROBE-LAUSCHEN <Play className="w-5 h-5 ml-1" fill="white" />
            </h2>
            <h3 className="text-[#343434] text-[2.125rem] md:text-[4.5rem] leading-[110%] tracking-[-0.06375rem] md:tracking-[-0.135rem] mb-8 md:mb-16 text-center font-freeman">  
              Ausschnitt aus Max' persönlicher Hörgeschichte
            </h3>
          </motion.div>
          
          {/* Audio Player Card */}
          <motion.div 
            variants={exampleCardVariants}
            className="bg-[#FFF3DD] rounded-3xl p-4 md:p-6 shadow-lg max-w-3xl mx-auto"
          >
            <div className="flex flex-col gap-2">
              {/* Top Row - Cover Image and Title */}
              <div className="flex gap-4 items-center">
                {/* Cover Image */}
                <div className="w-32 h-32 rounded-2xl bg-[#ECC4A3] overflow-hidden">
                  <img 
                    src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/01-Tg5ZGqvpjxErai0lMqaHkGclvKqwie.png"
                    alt="Story Cover"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                {/* Title and Tags */}
                <div className="flex-1 flex flex-col justify-center">
                  <h4 className="font-freeman text-2xl mb-2 text-[#343434] leading-[110%] tracking-[-0.03rem]">Max Abenteuer im Flüsterwald</h4>
                  <div className="flex flex-wrap gap-1.5">
                    <span className="bg-[#ffffff] text-[#343434] px-2.5 py-1.5 rounded-full font-['Aleo'] text-[0.75rem] font-medium leading-none tracking-[-0.00875rem] flex items-center">
                      <span className="translate-y-[0.5px]">Fantasie</span>
                    </span>
                    <span className="bg-[#ffffff] text-[#343434] px-2.5 py-1.5 rounded-full font-['Aleo'] text-[0.75rem] font-medium leading-none tracking-[-0.00875rem] flex items-center">
                      <span className="translate-y-[0.5px]">Ab 4 Jahren</span>
                    </span>
                    <span className="bg-[#ffffff] text-[#343434] px-2.5 py-1.5 rounded-full font-['Aleo'] text-[0.75rem] font-medium leading-none tracking-[-0.00875rem] flex items-center">
                      <span className="translate-y-[0.5px]">5 Minuten</span>
                    </span>
                  </div>
                </div>
              </div>

              {/* Bottom Row - Audio Controls */}
              <div className="flex items-center gap-4">
                <div
                  ref={progressBarRef}
                  className="h-1 bg-[#E8B489] rounded-full overflow-hidden w-full"
                  onMouseDown={(e) => {
                    setIsScrubbing(true);
                    handleScrub(e);
                  }}
                >
                  <div
                    className="h-full bg-[#7785FC] rounded-full transition-all duration-200"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
                
                <button 
                  onClick={handleExamplePlayClick}
                  className="w-12 h-12 bg-[#7785FC] text-white rounded-full flex items-center justify-center hover:bg-[#6674eb] transition-colors flex-shrink-0"
                >
                  {isPlaying ? (
                    <Pause size={24} className="fill-current" />
                  ) : (
                    <Play size={24} className="fill-current" />
                  )}
                </button>
              </div>

              {/* Add Script Text */}
              <div className="mt-1 relative">
                <p className="font-['Aleo'] text-[1rem] font-normal leading-[160%] text-[#343434] lg:w-[90%]">
                  <strong>Kapitel 1: Ein neuer Freund</strong><br />
                  Max liebte es, draußen zu spielen und sich kleine Abenteuer auszudenken. Eines Morgens, als er in seinem Garten kletterte, entdeckte er plötzlich etwas Seltsames: Ein kleiner, goldener Schlüssel lag versteckt zwischen den Wurzeln eines alten Baumes. Neben dem Schlüssel befand sich ein Zettel mit einer geheimnisvollen Botschaft:<br /><br />
                  <span className="hidden lg:inline">„Nur die Mutigsten dürfen den Flüsterwald betreten. Folge den Pfotenabdrücken, und dein Abenteuer beginnt."</span>
                </p>
                <div className="absolute bottom-0 left-0 right-0 h-64 lg:h-40 bg-gradient-to-t from-[#FFF3DD] to-transparent pointer-events-none"></div>
              </div>
            </div>
          </motion.div>

          {/* Add the new text below the card */}
          <motion.p
            variants={exampleCardVariants}
            className="text-center max-w-xl mx-auto text-[#343434] font-aleo text-[1rem] mt-6"
          >
            So ähnlich könnte deine Plottie-Geschichte aussehen. Jede Geschichte wird individuell erstellt und auf dein Kind zugeschnitten.
          </motion.p>
        </motion.div>
      </div>

      {/* Why Plottie section */}
      <div id="warum-plottie" className="w-screen relative left-[50%] right-[50%] mx-[-50vw]">
        {/* Decorative top divider - responsive images */}
        <div className="w-screen translate-y-[-1px]">
          {/* Mobile divider */}
          <img 
            src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Section-Banner_Top-Orange-W2ZYP9ED9O6V7uhXp0vrc5HbOeYJHe.png"
            className="w-full pointer-events-none select-none lg:hidden"
            aria-hidden="true"
            alt=""
            loading="lazy"
          />
          {/* Desktop divider */}
          <img 
            src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Section-Banner_top-orange-large-qHHGE5EHIvW76G8qCV92vcUjxfhjux.png"
            className="w-full pointer-events-none select-none hidden lg:block"
            aria-hidden="true"
            alt=""
            loading="lazy"
          />
        </div>

        {/* Main content */}
        <div className="relative">
          <div className="max-w-full sm:max-w-[90%] xl:max-w-[100rem] mx-auto px-4 sm:px-6 pt-12 md:pt-40">
            {/* Header content */}
            <div className="lg:hidden flex justify-between items-center mb-6">
              <h2 className="text-[#343434] text-[1.125rem] md:text-[1.5rem] font-bold leading-normal font-['Edu_TAS_Beginner'] pl-1">
                GANZ EINFACH
              </h2>
              <div className="flex items-center bg-[#FFF3DD] rounded-full p-1">
                <button 
                  onClick={handleScrollLeft}
                  disabled={!canScrollLeft}
                  className={`w-[36px] h-[36px] rounded-full flex items-center justify-center transition-colors duration-20 mr-1 ${
                    canScrollLeft 
                      ? 'bg-[#FFA23E] hover:bg-[#ff9625]' 
                      : 'bg-transparent'
                  }`}
                >
                  <ChevronLeft size={28} className="text-[#343434] relative left-[-1px]" />
                </button>
                <button 
                  onClick={handleScrollRight}
                  disabled={!canScrollRight}
                  className={`w-[36px] h-[36px] rounded-full flex items-center justify-center transition-colors duration-20 ${
                    canScrollRight 
                      ? 'bg-[#FFA23E] hover:bg-[#ff9625]' 
                      : 'bg-transparent'
                  }`}
                >
                  <ChevronRight size={28} className="text-[#343434] relative left-[1px]" />
                </button>
              </div>
            </div>

            {/* Desktop header */}
            <div className="hidden lg:block text-center mb-16">
              <h2 className="text-[#7785FC] text-[1.5rem] font-bold leading-normal font-['Edu_TAS_Beginner'] mb-2">
                GESCHICHTEN VON PLOTTIE SIND…
              </h2>
              <h3 className="text-[#343434] text-[4.5rem] leading-[110%] tracking-[-0.135rem] font-freeman relative">
                <RotatingText />
              </h3>
            </div>

            {/* Card Carousel */}
            <div 
              ref={carouselRef}
              className="flex gap-2 overflow-x-auto scrollbar-hidden snap-x snap-mandatory -mx-4 px-4 pb-8 sm:-mx-6 sm:px-6 xl:-mx-6 xl:px-6 lg:grid lg:grid-cols-4 lg:gap-6 lg:overflow-visible lg:snap-none lg:pb-16"
              style={{ scrollSnapType: 'x mandatory' }}
            >
              {[
                {
                  title: "Jedes Kind ist Held*in der eigenen Geschichte.",
                  description: "Keine vorgefertigten Märchen – hier steht dein Kind im Mittelpunkt. Plottie erstellt die Geschichten, so dass sie genau zu deinem Kind passen.",
                  titleColor: "#FFA23E"
                },
                {
                  title: "Aus kleinen Momenten werden große Abenteuer.",
                  description: "Ob die erste Begegnung mit einem Hund oder die Liebe zu einem bestimmten Spielzeug – Plottie verwandelt echte Erlebnisse in magische Geschichten.",
                  titleColor: "#7785FC"
                },
                {
                  title: "Freundschaft, Mut oder einfach Spaß haben.",
                  description: "Du bestimmst die Botschaft deiner Geschichte. Wir erstellen die Geschichten so, dass sie genau zu deinem Kind passen.",
                  titleColor: "#FFA23E"
                },
                {
                  title: "Starten, Stoppen, Wiederholen – wie du willst.",
                  description: "Spiele die Hörgeschichten direkt über deinen Lautsprecher ab oder übertrage sie auf deinen Kreativ-Tonie.",
                  titleColor: "#7785FC"
                }
              ].map((card, index) => (
                <div 
                  key={index}
                  className={`carousel-card w-[23.125rem] h-[29.875rem] flex-shrink-0 bg-[#FFF3DD] rounded-[1.5rem] snap-center mr-2 last:mr-0 relative overflow-hidden lg:w-full group lg:aspect-[4/5]`}
                  style={{ 
                    paddingTop: '1rem', 
                    paddingLeft: '1.25rem', 
                    paddingRight: '1.5rem', 
                    paddingBottom: '1.5rem'
                  }}
                >
                  <div className="relative z-10">
                    <h4 
                      className="font-freeman text-[2.5rem] font-normal leading-[110%] tracking-[-0.075rem] mt-1 mb-4 no-break"
                      style={{
                        leadingTrim: 'both',
                        textEdge: 'cap',
                        fontStyle: 'normal',
                        color: card.titleColor
                      }}
                    >
                      {card.title}
                    </h4>
                    <p className="font-aleo text-[#343434] leading-relaxed no-break">
                      {card.description}
                    </p>
                  </div>
                  <img 
                    src={`/why-${index + 1}.png`}
                    alt=""
                    className={`absolute bottom-0 left-0 w-full object-contain mix-blend-multiply transition-transform duration-500 ease-in-out origin-bottom ${
                      index === activeCardIndex ? 'scale-100' : 'scale-95'
                    } lg:group-hover:scale-105 origin-bottom`}
                    aria-hidden="true"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>

            {/* Add the button here */}
            <div className="flex justify-center mb-20 lg:mb-80">
              <a 
                href="/erstellen"
                onClick={() => {
                  trackEvent('engagement', 'create_story_button_how_it_works', 1);
                }}
                className="h-14 bg-[#FFA23E] text-[#343434] px-8 py-2 rounded-full font-freeman text-[1.25rem] font-normal leading-[110%] tracking-[-0.0375rem] inline-flex items-center justify-center hover:bg-[#FF9925] transition-colors duration-300"
              >
                Jetzt Geschichte erstellen
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div id="faq" className="max-w-full sm:max-w-[90%] xl:max-w-[70rem] mx-auto bg-[#EDD1BA] py-20">
        <div className="max-w-full mx-auto px-4 sm:px-6">
          {/* Use flex container for desktop layout */}
          <div className="flex flex-col lg:flex-row lg:gap-16 lg:items-start">
            {/* Left column - Headlines */}
            <div className="mb-16 lg:mb-0 lg:w-[40%] lg:sticky lg:top-32">
              <h2 className="text-[#7785FC] text-[1.125rem] md:text-[1.5rem] font-bold leading-normal font-['Edu_TAS_Beginner'] mb-2">
                HIER SIND ANTWORTEN
              </h2>
              <h3 className="text-[#343434] text-[2.125rem] md:text-[4.5rem] leading-[110%] tracking-[-0.06375rem] md:tracking-[-0.135rem] font-freeman">
                Das möchten <br /> Eltern wissen
              </h3>
            </div>
            
            {/* Right column - FAQ Accordion */}
            <div className="lg:w-[60%]">
              <div className="space-y-1">
                {faqs.map((faq, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                  >
                    <FAQAccordion
                      question={faq.question}
                      answer={faq.answer}
                      isOpen={openFaqIndex === index}
                      onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                    />
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add the Footer component here */}
      <Footer />

      <HomeStructuredData />

      {/* Add the audio element */}
      <audio 
        ref={audioRef} 
        src="/Abenteuer-im-Fluesterwald.m4a"
        onEnded={() => {
          setIsAudioPlaying(false);
          setAudioProgress(0);
          setActiveAudioIndex(null);
        }} 
      />
    </div>
  );
}
export default Landing; 
